import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, Divider, NumberInput, Select, Text, TextInput, Title } from "@mantine/core";
import { Field, FieldProps, useFormikContext } from "formik";
import { get } from "../../_api";

/**
 * Blok „Menu i strona startowa”, wspólny dla formularza strony i oferty.
 *
 * Menu nie ma osobnego modułu — składa się z treści, która sama deklaruje,
 * gdzie wisi. Pozycja mająca dzieci JEST kategorią; zwykle ma własną stronę,
 * a gdy nie ma, w menu tylko rozwija podmenu.
 */

export type MenuKind = "offer" | "page";

interface MenuNode {
  key: string; // "offer:<id>" albo "page:<id>"
  label: string;
  href: string | null;
  children: MenuNode[];
}

interface Props {
  /** Rodzaj edytowanej treści. */
  kind: MenuKind;
  /** `_id` edytowanej treści. Puste przy tworzeniu nowej. */
  selfId?: string;
}

/** Spłaszcza drzewo do listy z wcięciami, pomijając edytowaną pozycję i jej potomków. */
function toOptions(tree: MenuNode[], selfKey: string | null) {
  const out: { value: string; label: string }[] = [];

  const walk = (nodes: MenuNode[], depth: number) => {
    for (const n of nodes) {
      // Pominięcie samej siebie ucina CAŁĄ gałąź poniżej, więc potomek nie da
      // się wybrać na rodzica — menu nie da się zapętlić z poziomu panelu.
      if (n.key === selfKey) continue;
      out.push({ value: n.key, label: `${"  ".repeat(depth)}${depth ? "└ " : ""}${n.label}` });
      walk(n.children, depth + 1);
    }
  };

  walk(tree, 0);
  return out;
}

const MenuPlacement: React.FC<Props> = ({ kind, selfId }) => {
  const formik = useFormikContext<any>();
  const [tree, setTree] = useState<MenuNode[]>([]);
  const [blad, setBlad] = useState<string | null>(null);

  useEffect(() => {
    get("menu")
      .then((res: MenuNode[]) => setTree(Array.isArray(res) ? res : []))
      .catch(() => setBlad("Nie udało się pobrać listy pozycji menu."));
  }, []);

  const selfKey = selfId ? `${kind}:${selfId}` : null;
  const opcje = useMemo(() => toOptions(tree, selfKey), [tree, selfKey]);

  const pokazane = formik.values?.menu?.show;

  return (
    <>
      <Divider />
      <Title order={3}>Menu i strona startowa</Title>
      <Text size="sm" c="dimmed">
        Menu buduje się z treści. Wskaż, pod czym ta pozycja ma wisieć — pozycja,
        która ma pod sobą inne, staje się kategorią.
      </Text>

      <Field name="menu.show">
        {({ field, form }: FieldProps) => (
          <Checkbox
            label="Pokaż w menu"
            checked={!!field.value}
            onChange={(e) => form.setFieldValue(field.name, e.currentTarget.checked)}
          />
        )}
      </Field>

      {pokazane && (
        <>
          <Field name="menu.parentKey">
            {({ field, form }: FieldProps) => (
              <Select
                label="Pozycja nadrzędna"
                description={
                  blad ??
                  "Puste = pozycja najwyższego poziomu, czyli bezpośrednio w „Ofercie”."
                }
                error={blad ?? undefined}
                data={opcje}
                value={field.value || null}
                onChange={(v) => form.setFieldValue(field.name, v ?? "")}
                clearable
                searchable
                nothingFoundMessage="Brak pasującej pozycji"
              />
            )}
          </Field>

          <Field name="menu.label">
            {({ field }: FieldProps) => (
              <TextInput
                label="Etykieta w menu"
                description="Krótka nazwa. Puste = nazwa treści, która bywa długa i pisana pod wyszukiwarkę."
                {...field}
                value={field.value ?? ""}
              />
            )}
          </Field>

          <Field name="menu.order">
            {({ field, form }: FieldProps) => (
              <NumberInput
                label="Kolejność w menu"
                description="Mniejsza liczba = wyżej wśród pozycji o tym samym rodzicu."
                value={field.value ?? 0}
                onChange={(v) => form.setFieldValue(field.name, Number(v) || 0)}
              />
            )}
          </Field>
        </>
      )}

      <Divider />

      <Field name="featured.show">
        {({ field, form }: FieldProps) => (
          <Checkbox
            label="Główna — pokaż na stronie startowej"
            description="Sekcja OFERTA na rite.pl. Mieści 12 pozycji; nadmiarowe nie będą widoczne."
            checked={!!field.value}
            onChange={(e) => form.setFieldValue(field.name, e.currentTarget.checked)}
          />
        )}
      </Field>

      {formik.values?.featured?.show && (
        <Field name="featured.order">
          {({ field, form }: FieldProps) => (
            <NumberInput
              label="Kolejność na stronie startowej"
              description="Osobna od kolejności w menu — ta lista jest płaska i miesza gałęzie."
              value={field.value ?? 0}
              onChange={(v) => form.setFieldValue(field.name, Number(v) || 0)}
            />
          )}
        </Field>
      )}
    </>
  );
};

export default MenuPlacement;

/** Zamiana wartości z formularza na kształt oczekiwany przez API. */
export function menuPayload(values: any) {
  const key: string = values?.menu?.parentKey || "";
  const [kind, id] = key.split(":");

  return {
    menu: {
      show: !!values?.menu?.show,
      label: values?.menu?.label || "",
      order: Number(values?.menu?.order) || 0,
      parent: id ? { kind, id } : null,
    },
    featured: {
      show: !!values?.featured?.show,
      order: Number(values?.featured?.order) || 0,
    },
  };
}

/** Odwrotność `menuPayload` — przygotowanie wartości początkowych formularza. */
export function menuInitial(doc: any) {
  const p = doc?.menu?.parent;
  return {
    menu: {
      show: !!doc?.menu?.show,
      parentKey: p?.id ? `${p.kind}:${p.id}` : "",
      label: doc?.menu?.label ?? "",
      order: doc?.menu?.order ?? 0,
    },
    featured: {
      show: !!doc?.featured?.show,
      order: doc?.featured?.order ?? 0,
    },
  };
}
